import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'

// The message composer. Adapted from nut.new's MessageInput: a bordered container that wraps an
// auto-growing, borderless textarea over a bottom control bar with a pill "Send" button. Enter sends
// (Shift+Enter inserts a newline), and the send button is disabled while the agent is replying or the
// field is empty.
//
// The same composer backs the onboarding landing (OnboardingChat's URL entry) so the initial
// experience matches nut.new's BaseChat prompt — hence the small knobs below: `submitLabel` /
// `submitHint` relabel the send button, `initialValue` pre-fills it (e.g. from a ?url= param), and
// `clearOnSend` keeps the text on submit so a validation failure doesn't wipe what was typed.

export default function ChatInput({
  onSend,
  disabled,
  placeholder = 'Type a message…',
  autoFocus,
  submitLabel = 'Send',
  submitHint,
  initialValue = '',
  clearOnSend = true,
  inline = false,
  fillSignal,
}: {
  onSend: (text: string) => void
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  submitLabel?: string
  // Trailing content on the send button — defaults to the "⏎" keyboard hint; pass an icon (e.g. an
  // arrow) for the onboarding "Start setup" action.
  submitHint?: ReactNode
  initialValue?: string
  clearOnSend?: boolean
  // Single-row layout: the field and the send button sit side by side rather than the button on a
  // control bar below. Used for the onboarding landing where only a one-line URL is needed, so the
  // composer doesn't read as two lines. The field stays single-line (long URLs scroll horizontally).
  inline?: boolean
  // An external request to fill the composer (without sending) — keyed by `seq` so a repeated fill
  // with the same text still applies. Backs the settings page's "Change in chat" deep-links.
  fillSignal?: { text: string; seq: number }
}) {
  const [value, setValue] = useState(initialValue)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Apply an external fill request: set the text and focus, but do NOT send. The caller hands a fresh
  // object (new `seq`) for every fill, so depending on `fillSignal` re-runs even when the text repeats.
  useEffect(() => {
    if (!fillSignal) return
    setValue(fillSignal.text)
    textareaRef.current?.focus()
  }, [fillSignal])

  // Grow the textarea with its content, up to a cap (then it scrolls).
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [value])

  const submit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    if (clearOnSend) setValue('')
    onSend(trimmed)
  }, [value, disabled, onSend, clearOnSend])

  // The visible container is larger than the textarea itself (it also holds the control bar and its
  // own padding). Treat a press anywhere in that empty space as a request to focus the textarea, so
  // the whole box is clickable — but leave clicks on the textarea and the Send button alone.
  const focusFromContainer = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target === textareaRef.current || target.closest('button')) return
    e.preventDefault() // keep the textarea from losing focus mid-click
    textareaRef.current?.focus()
  }, [])

  return (
    <div className={`chat-input${inline ? ' chat-input--inline' : ''}`} data-testid="chat-input" onMouseDown={focusFromContainer}>
      <textarea
        ref={textareaRef}
        className="chat-input__textarea"
        value={value}
        rows={1}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            submit()
          }
        }}
        autoFocus={autoFocus}
        aria-label="Chat message"
        data-testid="chat-input-textarea"
        data-analytics-event="chat-input-type"
      />
      <div className="chat-input__bar">
        <button
          type="button"
          className="chat-input__send"
          onClick={submit}
          disabled={disabled || !value.trim()}
          aria-label="Send message"
          data-testid="chat-send-btn"
          data-analytics-event="chat-input-send"
        >
          <span>{submitLabel}</span>
          {submitHint ?? <span className="chat-input__send-hint" aria-hidden>⏎</span>}
        </button>
      </div>
    </div>
  )
}
